import { createSlice } from '@reduxjs/toolkit'
import * as Yup from 'yup'

import { initYup } from "utils/helpers";
initYup();

type AddItemPayload<K extends keyof HeatModeForm> = {
  name: K
  value: HeatModeForm[K]
}

export const heatModeSchema = Yup.object({
  departement: Yup.string().required(),
  stationMeteo: Yup.string().required(),
  altitude: Yup.number().integer().required(),
  placesaid: Yup.string(),
  seaside: Yup.boolean().required(),
  tbase: Yup.number(),
  tbaseModified: Yup.boolean(),
  mitoyennete: Yup.number().integer().when('deperditions', {
    is: (deperditions: any) => deperditions?.deperditionMethod !== '4',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
  }),
  level: Yup.number().integer().when('deperditions', {
    is: (deperditions: any) => deperditions?.deperditionMethod !== '4',
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.nullable(),
  }),
  stationsList: Yup.array(),
  departmentsList: Yup.array(),
  zones: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().nullable(),
        surface: Yup.number().required(),
        hauteur: Yup.number().required(),
        emetteur: Yup.number().required(),
        temperatureNeeded: Yup.boolean(),
        temperature: Yup.mixed().when('temperatureNeeded', {
          is: true,
          then: () =>
            Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          otherwise: () => Yup.mixed().nullable(),
        }),
        detail: Yup.array().when('emetteur_i18nCode', {
          is: (code: string) => code === 'SELECT_Z1_DESCRIPTION_DETAILLEE' || code === 'SELECT_Z2_DESCRIPTION_DETAILLEE',
          then: () => Yup.array().of(
            Yup.object({
              id: Yup.string(),
              nombreEmeteur: Yup.number().required(),
              radiatorClass: Yup.string().required(),
              radiatorModel: Yup.mixed().when('radiatorClass', {
                is: (radiatorClass: string) => radiatorClass === "Radiateurs sèche serviettes" || radiatorClass === "Radiateur multi colonnes vertical acier",
                then: () => Yup.string().nullable(),
                otherwise: () => Yup.string().required(),
              }),
              width: Yup.number().required(),
              length: Yup.number().required(),
            })
          ),
          otherwise: () => Yup.array().of(
            Yup.object({
              id: Yup.string(),
              nombreEmeteur: Yup.number().nullable(),
              radiatorClass: Yup.string().nullable(),
              radiatorModel: Yup.string().nullable(),
              width: Yup.number().nullable(),
              length: Yup.number().nullable(),
            })
          )
        }),
      }),
    )
    .min(1, 'Au moins une zone est requise'),

  deperditions: Yup.lazy((value) => {
    if (value !== undefined) {
      switch (value.deperditionMethod) {
        case '0': // Connues
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            deperditions: Yup.number()
              .integer()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          })

        case '1': // DPE
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            dpe: Yup.number()
              .integer()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
          })

        case '2': // Invoice
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            annualBilling: Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            actualSystem: Yup.string().required(),
            nrgPrice: Yup.number()
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            withECS: Yup.string().required(),
            nbPeoples: Yup.mixed().when('withECS', {
              is: '1',
              then: () => Yup.string().required('Nombre de personnes requis'),
              otherwise: () => Yup.mixed(),
            }),
            systemAge: Yup.string().required(),
            dayTemp: Yup.string().required(),
            nightTemp: Yup.string().required(),
          })

        case '3': // Coefficient
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            //year: Yup.number().min(100, "ERROR_YEAR").max(2035, "ERROR_YEAR").required("ERROR_YEAR"),
            year: Yup.number()
              .min(100)
              .max(2035)
              .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
              .required(),
            insulationWork: Yup.string().required(),
            walls: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Murs requis'),
              otherwise: () => Yup.mixed(),
            }),
            roofing: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Toiture requise'),
              otherwise: () => Yup.mixed(),
            }),
            windows: Yup.mixed().when('insulationWork', {
              is: '1',
              then: () => Yup.string().required('Fenêtres requises'),
              otherwise: () => Yup.mixed(),
            }),
          })

        case '4': // Expert
          return Yup.object({
            deperditionMethod: Yup.string().required(),
            temperature: Yup.number().required(),
            houseType: Yup.string().required(),
            houseShape: Yup.string().required(),
            levels: Yup.number().required(),
            levels_motor: Yup.number().required(),
            adjacency: Yup.string().required(),
            homeAttics: Yup.boolean(),
            homeIsolation: Yup.boolean(),
            wallType: Yup.string().required(),
            wallSurface: Yup.number().when('wallSurfaceUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            wallSurfaceUnknown: Yup.boolean(),
            wallCoefficient: Yup.number().when('wallCoefficientUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            wallCoefficientUnknown: Yup.boolean(),
            wallThickness: Yup.number().when('wallThicknessUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            wallThicknessUnknown: Yup.boolean(),
            wallIsolated: Yup.boolean(),
            wallInsulationThickness: Yup.number().when(['wallIsolated', 'wallInsulationThicknessUnknown'], {
              is: (wallIsolated: boolean, wallInsulationThicknessUnknown: boolean) => wallIsolated && !wallInsulationThicknessUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            wallInsulationThicknessUnknown: Yup.boolean(),
            wallInsulationR: Yup.number().when(['wallIsolated', 'wallInsulationRUnknown'], {
              is: (wallIsolated: boolean, wallInsulationRUnknown: boolean) => wallIsolated && !wallInsulationRUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            wallInsulationRUnknown: Yup.boolean(),
            ceilingType: Yup.string().required(),
            roofSurface: Yup.number().when('roofSurfaceUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            roofSurfaceUnknown: Yup.boolean(),
            ceilingCoefficient: Yup.number().when('ceilingCoefficientUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            ceilingCoefficientUnknown: Yup.boolean(),
            ceilingIsolated: Yup.boolean(),
            ceilingInsulationThickness: Yup.number().when(['ceilingIsolated', 'ceilingInsulationThicknessUnknown'], {
              is: (ceilingIsolated: boolean, ceilingInsulationThicknessUnknown: boolean) => ceilingIsolated && !ceilingInsulationThicknessUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            ceilingInsulationThicknessUnknown: Yup.boolean(),
            ceilingInsulationR: Yup.number().when(['ceilingIsolated', 'ceilingInsulationRUnknown'], {
              is: (ceilingIsolated: boolean, ceilingInsulationRUnknown: boolean) => ceilingIsolated && !ceilingInsulationRUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            ceilingInsulationRUnknown: Yup.boolean(),
            atticType: Yup.string().required(),
            floorType: Yup.string().required(),
            foundationType: Yup.string().required(),
            floorSurface: Yup.number().when('floorSurfaceUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            floorSurfaceUnknown: Yup.boolean(),
            floorCoefficient: Yup.number().when('floorCoefficientUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            floorCoefficientUnknown: Yup.boolean(),
            floorInsulationR: Yup.number().when(['floorIsolated', 'floorInsulationRUnknown'], {
              is: (floorIsolated: boolean, floorInsulationRUnknown: boolean) => floorIsolated && !floorInsulationRUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            floorInsulationRUnknown: Yup.boolean(),
            floorIsolated: Yup.boolean(),
            floorInsulationThickness: Yup.number().when(['floorIsolated', 'floorInsulationThicknessUnknown'], {
              is: (floorIsolated: boolean, floorInsulationThicknessUnknown: boolean) => floorIsolated && !floorInsulationThicknessUnknown,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            floorInsulationThicknessUnknown: Yup.boolean(),
            windowType: Yup.string().required(),
            windowMaterial: Yup.string().required(),
            windowSurface: Yup.number().when('windowSurfaceUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            windowSurfaceUnknown: Yup.boolean(),
            windowCoefficient: Yup.number().when('windowCoefficientUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            windowCoefficientUnknown: Yup.boolean(),
            doorUValue: Yup.string().required(),
            openingDoorWidth: Yup.number().nullable(),
            openingDoorHeight: Yup.number().nullable(),
            doorCoefficient: Yup.number().when('doorCoefficientUnknown', {
              is: false,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            doorCoefficientUnknown: Yup.boolean(),
            ventilationType: Yup.string().nullable(),
          })

        default:
          return Yup.object({
            deperditionMethod: Yup.string().required(),
          })
      }
    }

    // fallback
    return Yup.mixed().notRequired()
  }),
})

type EmetteurDetail = {
  id?: string,
  nombreEmeteur?: number,
  radiatorClass?: string,
  radiatorModel?: string,
  width?: number | null,
  length?: number | null,
}

export const defaultEmetteurDetail: EmetteurDetail = {
  id: Date.now().toString(),
  nombreEmeteur: 1,
  radiatorClass: '',
  radiatorModel: '',
  width: undefined,
  length: undefined,
}

type Zone = {
  surface?: number
  hauteur: number
  emetteur?: any
  emetteur_i18nCode: string
  temperatureNeeded: boolean
  temperature: string
  detail?: EmetteurDetail[]
}

export const defaultZone: Zone = {
  surface: undefined,
  hauteur: 2.5,
  emetteur: undefined,
  emetteur_i18nCode: '',
  temperatureNeeded: true,
  temperature: '',
  detail: [defaultEmetteurDetail],
}

export const defaultForm = {
  departement: undefined,
  departement_value: '',
  stationMeteo: undefined,
  stationMeteo_value: '',
  altitude: undefined,
  placesaid: '',
  seaside: false,
  tbase: undefined,
  tbaseModified: false,
  mitoyennete: 0,
  mitoyennete_i18nCode: '0_WALL',
  level: 0,
  level_i18nCode: '0_LEVEL',
  zones: [{ ...defaultZone }] as Zone[],
  // zones: [defaultZone],
  deperditions: {
    deperditionMethod: undefined,
    deperditionMethod_i18nCode: '',
    temperature: '',
    houseType: 'Maison',
    houseShape: '1',
    levels: null,
    levels_motor: null,
    adjacency: '0',
    homeAttics: false,
    homeIsolation: false,
    wallType: '',
    wallSurface: null,
    wallSurfaceUnknown: false,
    wallCoefficient: null,
    wallCoefficientUnknown: true,
    wallThickness: null,
    wallThicknessUnknown: false,
    wallIsolated: false,
    wallInsulationThickness: null,
    wallInsulationThicknessUnknown: false,
    ceilingType: '',
    roofSurface: null,
    roofSurfaceUnknown: false,
    ceilingCoefficient: null,
    ceilingCoefficientUnknown: true,
    ceilingIsolated: false,
    ceilingInsulationThickness: null,
    ceilingInsulationThicknessUnknown: false,
    atticType: '',
    floorType: '',
    foundationType: '',
    floorSurface: null,
    floorSurfaceUnknown: false,
    floorCoefficient: null,
    floorCoefficientUnknown: true,
    floorIsolated: false,
    floorInsulationThickness: null,
    floorInsulationThicknessUnknown: false,
    windowType: '',
    windowMaterial: '',
    windowSurface: null,
    windowSurfaceUnknown: false,
    windowCoefficient: null,
    windowCoefficientUnknown: true,
    doorUValue: 'inconnu',
    openingDoorWidth: 90,
    openingDoorHeight: 215,
    doorCoefficient: null,
    doorCoefficientUnknown: true,
    ventilationType: '',
    deperditions: '',
    year: '',
    insulationWork: 1,
    // walls: undefined,
    walls: '',
    walls_i18nCode: '',
    // roofing: undefined,
    roofing: '',
    roofing_i18nCode: '',
    // windows: undefined,
    windows: '',
    windows_i18nCode: '',
    dpe: '',
    annualBilling: '',
    actualSystem: undefined,
    actualSystem_i18nCode: '',
    nrgPrice: '',
    nrgUnit: '',
    withECS: undefined,
    withECS_i18nCode: '',
    nbPeoples: undefined,
    systemAge: undefined,
    systemAge_i18nCode: '',
    dayTemp: '',
    nightTemp: '',
    loss: {},
  },
  stationsList: [] as ValueListItem[],
  departmentsList: [] as ValueListItem[],
}

const initialState = {
  form: { ...defaultForm },
}

export type HeatModeForm = typeof defaultForm

export const heatModeSlice = createSlice({
  name: 'heatMode',
  initialState,
  reducers: {
    initNewProject(state) {
      state.form = { ...defaultForm }
      state.form.zones = [{ ...defaultZone }]
    },
    updateMitoyennete(state, action) {
      state.form.mitoyennete = action.payload.code
      state.form.mitoyennete_i18nCode = action.payload.label
    },
    updateLevel(state, action) {
      state.form.level = action.payload.code
      state.form.level_i18nCode = action.payload.label
    },
    addZone(state) {
      state.form.zones.push({ ...defaultZone })
    },
    removeZone(state, action) {
      state.form.zones.splice(action.payload, 1)
    },
    updateTbase(state, action) {
      state.form.tbase = action.payload
      state.form.tbaseModified = false
    },
    addItem<K extends keyof HeatModeForm>(state: any, action: { payload: AddItemPayload<K> }) {
      const { name, value } = action.payload
      state.form[name] = value
    },
    load(state, action) {
      state.form = { ...action.payload }
    },
  },
})

export const { initNewProject, updateMitoyennete, updateLevel, addZone, removeZone, updateTbase, load, addItem } = heatModeSlice.actions

export default heatModeSlice.reducer
